import { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import dayjs from "dayjs";
import * as Yup from "yup";
import {
  Box,
  CircularProgress,
  Container,
  Grid2 as Grid,
  Card,
  Typography,
  List,
  CardHeader,
  CardContent,
} from "@mui/material";

import ListItemWithPopup from "./dashboardComponents/ListItemWithPopup";
import NotaryAvailability from "./dashboardComponents/NotaryAvailability";
import { fetchNotaryData } from "../../store/actions/notaryActions";

const FinishNotary = () => {
  const countRef = useRef(0);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.notarySession.user);
  const currentSession = useSelector((state) => state.sessions.currentSession);
  const [loadingPage, setLoadingPage] = useState(true);
  const [availabilities, setAvailabilities] = useState([]);
  const [saveLoading, setSaveLoading] = useState(false);
  const [fetching, setFetching] = useState(false);

  const daysOfWeek = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ];

  // Pre-fill all days and times on initial load
  const [selectedDays, setSelectedDays] = useState(daysOfWeek);
  const [selectedStartTime, setSelectedStartTime] = useState(
    dayjs().hour(0).minute(30)
  ); // 12:30 AM
  const [selectedEndTime, setSelectedEndTime] = useState(
    dayjs().hour(23).minute(30)
  ); // 11:30 PM
  const [prefillLoading, setPrefillLoading] = useState(false);

  const handleFetch = async () => {
    setFetching(true);
    const data = await dispatch(fetchNotaryData(currentSession));
    setFetching(false);
  };

  const daySchema = Yup.object()
    .shape({
      day: Yup.string(),
      available: Yup.boolean(),
      startTime: Yup.date()
        .nullable()
        .typeError("Start time must be a valid date and time"),
      endTime: Yup.date()
        .nullable()
        .typeError("End time must be a valid date and time"),
    })
    .test("time-conditional", null, function (obj) {
      const { available, startTime, endTime } = obj;

      if (!available) return true;

      const start = new Date(startTime);
      const end = new Date(endTime);

      if (!startTime || isNaN(start.getTime())) {
        return this.createError({
          path: `${this.path}.startTime`,
          message: "Start time is required",
        });
      }

      if (!endTime || isNaN(end.getTime())) {
        return this.createError({
          path: `${this.path}.endTime`,
          message: "End time is required",
        });
      }

      if (!isValidTime(start)) {
        return this.createError({
          path: `${this.path}.startTime`,
          message: "Start time must be on the hour or half-hour",
        });
      }

      if (!isValidTime(end)) {
        return this.createError({
          path: `${this.path}.endTime`,
          message: "End time must be on the hour or half-hour",
        });
      }

      // ❌ Disallow 12:00 PM for start time
      if (start.getHours() === 12 && start.getMinutes() === 0) {
        return this.createError({
          path: `${this.path}.startTime`,
          message: "12:00 PM is not allowed as a start time",
        });
      }

      // ❌ Disallow 12:00 PM for end time
      if (end.getHours() === 12 && end.getMinutes() === 0) {
        return this.createError({
          path: `${this.path}.endTime`,
          message: "12:00 PM is not allowed as an end time",
        });
      }

      const startMinutes = start.getHours() * 60 + start.getMinutes();
      const endMinutes = end.getHours() * 60 + end.getMinutes();

      if (
        endMinutes <= startMinutes &&
        !(end.getHours() === 0 && end.getMinutes() === 0)
      ) {
        return this.createError({
          path: `${this.path}.endTime`,
          message:
            "End time must be after start time unless it's exactly 00:00 marking the end of the day.",
        });
      }

      const hourDifference = ((end - start + 86400000) % 86400000) / 3600000;

      if (hourDifference < 1) {
        return this.createError({
          path: `${this.path}.endTime`,
          message:
            "There must be at least 1 hour difference between start and end times",
        });
      }

      return true;
    });

  const validationSchema = Yup.object({
    availability: Yup.array()
      .of(daySchema)
      .test(
        "total-hours",
        "Minimum of 1 hours per week required",
        function (availabilityList) {
          let totalMinutes = 0;
          for (const day of availabilityList) {
            if (day.available && day.startTime && day.endTime) {
              // Ensure startTime and endTime are Date objects
              const startTime = new Date(day.startTime);
              const endTime = new Date(day.endTime);
              // Check if the dates are valid
              if (!isNaN(startTime.getTime()) && !isNaN(endTime.getTime())) {
                const diff = endTime.getTime() - startTime.getTime();
                totalMinutes += diff / (1000 * 60);
              }
            }
          }
          const totalHours = totalMinutes / 60;
          return totalHours >= 1;
        }
      ),
  });

  const initialValues = {
    availability: daysOfWeek.map((day) => {
      const availabilityData = availabilities.find((a) => a.day === day);

      return availabilityData
        ? {
            day: day,
            available: availabilityData.available,
            startTime: createTimeFromString(availabilityData.startTime),
            endTime: createTimeFromString(availabilityData.endTime),
          }
        : {
            day: day,
            available: false,
            startTime: null,
            endTime: null,
          };
    }),
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: initialValues,
    validationSchema,
    // validateOnBlur: true, // Disable validation when losing focus
    // validateOnChange: false, // Disable validation on every change
    onSubmit: async (values) => {
      const processedAvailability = values.availability.map((day) => {
        return {
          ...day,
          startTime: day.startTime ? formatTime(day.startTime) : null,
          endTime: day.endTime ? formatTime(day.endTime) : null,
        };
      });

      try {
        setSaveLoading(true);
        const resp = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/nocApi/notary/save-availabilities`,
          { payload: processedAvailability, currentSession: currentSession },
          {
            withCredentials: true,
          }
        );
        if (resp.data.success) {
          setAvailabilities(processedAvailability);
          // Always fetch updated user data after saving
          dispatch(fetchNotaryData(currentSession));
          // Navigate to dashboard after successful save
          navigate("/notary/dashboard");
        }
        setSaveLoading(false);
      } catch (e) {
        setSaveLoading(false);
      }
    },
  });

  function isValidTime(time) {
    if (!time || isNaN(time.getTime())) return false;
    const minutes = time.getMinutes();
    return minutes === 0 || minutes === 30;
  }

  function formatTime(time) {
    const date = new Date(time);
    let hours = date.getHours();
    let minutes = date.getMinutes();

    // Format hours and minutes to ensure two digits
    hours = hours < 10 ? "0" + hours : hours;
    minutes = minutes < 10 ? "0" + minutes : minutes;

    return `${hours}:${minutes}`;
  }

  function createTimeFromString(timeString) {
    if (!timeString) return null;

    const [hours, minutes] = timeString.split(":").map(Number);
    // Create a Day.js object from the current date, then set the hours and minutes
    return dayjs().hour(hours).minute(minutes);
  }

  useEffect(() => {
    let cancelled = false;

    const getAvailabilities = async () => {
      try {
        const resp = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/nocApi/notary/notary-availability`,
          { currentSession: currentSession },
          { withCredentials: true }
        );

        if (!cancelled) {
          const fetchedData = resp.data.data;
          setAvailabilities(fetchedData);

          const hasAvailability = fetchedData.some((day) => day.available);

          if (hasAvailability) {
            navigate("/notary/dashboard");
          } else {
            setLoadingPage(false);
          }
        }
      } catch (e) {
        if (!cancelled) setLoadingPage(false);
      } finally {
        setLoadingPage(false);
      }
    };

    if (countRef.current < 1) {
      countRef.current += 1;
      getAvailabilities();
    }

    return () => {
      cancelled = true;
    };
  }, [currentUser]);

  // Check availabilities on component mount and when availabilities change
  useEffect(() => {
    // Check if user already has availabilities set
    if (currentUser?.availabilities && currentUser.availabilities.length > 0) {
      const hasAvailability = currentUser.availabilities.some(
        (day) => day.available
      );
      if (hasAvailability) {
        navigate("/notary/dashboard");
        return;
      }
    }

    // Check local availabilities state
    if (availabilities.length > 0) {
      const hasAvailability = availabilities.some((day) => day.available);
      if (hasAvailability) {
        navigate("/notary/dashboard");
        return;
      }
    }
  }, [currentUser, availabilities, navigate]);

  const handlePrefillTimes = () => {
    setPrefillLoading(true);
    const updatedAvailability = formik.values.availability.map((day) => {
      if (selectedDays.includes(day.day)) {
        return {
          ...day,
          available: true,
          startTime: selectedStartTime, // Ensure these are in the correct format
          endTime: selectedEndTime,
        };
      } else {
        // For days not included in selectedDays, set available to false and clear times
        return {
          ...day,
          available: false,
          startTime: null,
          endTime: null,
        };
      }
    });

    formik.setFieldValue("availability", updatedAvailability);
    // Reset the selected days and times after updating
    setSelectedDays([]);
    setSelectedStartTime(null);
    setSelectedEndTime(null);
    setPrefillLoading(false);
  };

  return (
    <>
      {loadingPage ? (
        <>
          <Box
            sx={{
              display: "flex",
              width: "100%",
              height: "100vh",
              justifyContent: "center",
              alignItems: "center",
              flexDirection: "column",
            }}
          >
            <CircularProgress color="warning" size="10rem" />
          </Box>
        </>
      ) : (
        <>
          <Box
            component="main"
            sx={{
              flexGrow: 1,
              py: 1,
              my: 2,
              backgroundColor: "#EEF2F6",
            }}
          >
            <Container maxWidth="xxl">
              <Grid container spacing={2} justifyContent="center">
                <Grid size={{ xs: 12 }}>
                  <Card sx={{ backgroundColor: "#FCF2F0", py: 4, my: 2 }}>
                    <Grid container px={6}>
                      <Grid size={{ sm: 9 }} sx={{ pt: 3 }}>
                        <Typography align="center" variant="h3">
                          Complete Your Notary Account Setup
                        </Typography>
                        <Typography
                          mt={2}
                          mb={8}
                          align="center"
                          variant="body1"
                        >
                          To begin your journey with us, it's essential to set
                          your RON session availability. Please configure your
                          preferred time frames below to commence.
                        </Typography>

                        <Box
                          sx={{
                            boxShadow: "6px 5px 14px #d3d3d3",
                            borderRadius: "1rem",
                            mt: "2rem",
                          }}
                        >
                          <Card>
                            <CardHeader title="Set Your RON Session Availability" />
                            <CardContent>
                              <NotaryAvailability
                                formik={formik}
                                availabilities={availabilities}
                                saveLoading={saveLoading}
                                selectedDays={selectedDays}
                                setSelectedDays={setSelectedDays}
                                selectedStartTime={selectedStartTime}
                                setSelectedStartTime={setSelectedStartTime}
                                selectedEndTime={selectedEndTime}
                                setSelectedEndTime={setSelectedEndTime}
                                prefillLoading={prefillLoading}
                                handlePrefillTimes={handlePrefillTimes}
                              />
                            </CardContent>
                          </Card>
                        </Box>
                      </Grid>
                      <Grid size={{ sm: 3 }} mt={20}>
                        <Box
                          sx={{
                            position: "fixed", // Fix the position relative to the viewport
                            top: "50%", // Position at the middle of the viewport vertically
                            transform: "translateY(-50%)", // Shift upwards by half its height to center
                            right: 30, // Align to the right edge of the viewport
                            width: "20%", // Adjust the width as needed
                            // If using a container, adjust the right value to align with the Grid
                          }}
                        >
                          <img
                            src="/img/logos/finalize_notary.png"
                            style={{ width: "100%", height: "auto" }}
                            alt="Notary Public Stamp"
                          />
                          {/* Other component content */}
                        </Box>
                      </Grid>
                    </Grid>
                  </Card>
                </Grid>
              </Grid>
            </Container>
          </Box>
        </>
      )}
    </>
  );
};

export default FinishNotary;
